/**
 * An envelope for the card (2026-10-17): choose one of the envelopes that come
 * out of one sheet of US Letter (envelope/designs.ts), put a name on its front,
 * keep the card's own paper on its outside or leave it white, and print it -
 * both sides at once, or each by hand, like the card. The choice is kept with
 * the card (ProjectDocument.envelope), so it travels in the card's file too.
 *
 * Nothing about it has been folded on paper yet: the page says so, and asks for
 * the result.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { ProjectDocument, TemplateDocument } from '@foldmycard/scene';
import { api, ApiError, type ProjectItem } from '../api.ts';
import { ENVELOPES, envelopeFor, type Envelope, type EnvelopeId } from '../envelope/designs.ts';
import { EnvelopeInside, EnvelopeOutside, EnvelopeThumb, EnvelopeView, type Decor } from '../envelope/sheet.tsx';
import { Link, useLocation } from '../router.tsx';
import { t, type StringKey } from '../strings.ts';
import { SignOutButton } from '../idle.tsx';
import { TipButton } from '../tip.tsx';

type Settings = NonNullable<ProjectDocument['envelope']>;

const lightness = (hex: string) => {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
  if (!m) return 1;
  const [r, g, b] = m.slice(1).map((h) => parseInt(h, 16) / 255);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};
/**
 * The colour of the name and the seal: the card's message colour, else its pattern's -
 * whichever is dark enough to read on the white label and stands out from the paper;
 * else a deep red, else a deep blue.
 */
function inkFor(candidates: (string | undefined)[], paper: string) {
  const ok = (c: string) => lightness(c) < 0.5 && Math.abs(lightness(c) - lightness(paper)) > 0.12;
  return [...candidates.filter((c): c is string => !!c), '#a3141f', '#1f4e8c'].find(ok) ?? '#1f4e8c';
}

export function EnvelopePage({ projectId }: { projectId: string }) {
  const q = useLocation().searchParams;
  const [project, setProject] = useState<ProjectItem | null>(null);
  const [template, setTemplate] = useState<TemplateDocument | null>(null);
  const [doc, setDoc] = useState<ProjectDocument | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [tab, setTab] = useState<'front' | 'back' | 'side1' | 'side2'>('front');
  const [only, setOnly] = useState<0 | 1 | 2>(0);
  const where = useRef<{ vid: string; revision: number } | null>(null);
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => {
    (async () => {
      const { project, versions } = await api.project(projectId);
      const vid = q.get('v') || [...versions].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))[0].id;
      const { version } = await api.version(projectId, vid);
      const tpl = await api.template(project.templateId, project.paper, version.templateHash);
      where.current = { vid, revision: version.revision };
      setProject(project);
      setTemplate(tpl);
      setDoc(version.document!);
    })().catch((err) => setError((err as Error).message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  /** The closed card's size: the design's front view. */
  const card = useMemo(() => {
    const v = template?.views.find((x) => x.id === 'f1');
    return v ? { w: v.widthMm, h: v.heightMm } : null;
  }, [template]);
  const options = useMemo(() => (card ? ENVELOPES.map((id) => ({ id, env: envelopeFor(id, card) })).filter((o): o is { id: EnvelopeId; env: Envelope } => !!o.env) : []), [card]);

  const settings: Settings = doc?.envelope ?? { style: 'classic' };
  const chosen = options.find((o) => o.id === settings.style) ?? options[0];

  /** Keep the choice with the card, a moment after the last change. */
  const update = (patch: Partial<Settings>) => {
    if (!doc) return;
    const next: ProjectDocument = { ...doc, envelope: { ...settings, ...patch } };
    setDoc(next);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(async () => {
      if (!where.current) return;
      try {
        const r = await api.saveVersion(projectId, where.current.vid, next, where.current.revision);
        where.current.revision = r.version.revision;
        setSaveError(null);
      } catch (err) {
        setSaveError(err instanceof ApiError && err.status === 409 ? t('conflict') : (err as Error).message);
      }
    }, 600);
  };

  if (error) return <main className="page"><p className="error">{error}</p></main>;
  if (project && doc && card && !options.length) {
    // too big for any envelope from one sheet (the photo frame): say what to buy instead
    return (
      <main className="page">
        <div className="print-top">
          <Link href={`/projects/${project.id}${q.get('v') ? `?v=${q.get('v')}` : ''}`} className="button back">← {t('backToCard')}</Link>
          <h1>{t('envTitle')}</h1>
        </div>
        <p>{t('envNone', { w: Math.round(Math.max(card.w, card.h)), h: Math.round(Math.min(card.w, card.h)) })}</p>
      </main>
    );
  }
  if (!project || !doc || !card || !chosen) return <main className="page"><p className="muted">{t('loading')}</p></main>;

  const env = chosen.env;
  const name = settings.name ?? '';
  const message = Object.values(doc.zones).find((z) => z.text?.content.trim())?.text;
  const decor: Decor = {
    paper: doc.paper, name, plain: !!settings.plain, mono: doc.mono,
    initial: [...name.trim()][0]?.toUpperCase() ?? '',
    ink: inkFor([message?.color, doc.paper?.ink], settings.plain ? '#ffffff' : doc.paper?.color ?? '#ffffff'),
  };
  const W = env.sheet.w, H = env.sheet.h;
  const v = q.get('v') ? `?v=${q.get('v')}` : '';
  const printSides = async (side: 0 | 1 | 2) => {
    setOnly(side);
    if (side) addEventListener('afterprint', () => setOnly(0), { once: true });
    setTimeout(() => window.print(), 150);
  };
  const long = Math.max(card.w, card.h), short = Math.min(card.w, card.h);
  const store = long <= 160 && short <= 115 ? t('envStoreA6') : long <= 120 ? t('envStoreSquare') : null;

  return (
    <div className="print-root envelope-root">
      <style>{`@page { size: ${W}mm ${H}mm; margin: 0; }`}</style>
      <main className="envelope-page no-print">
        <div className="print-top">
          <Link href={`/projects/${project.id}${v}`} className="button back">← {t('backToCard')}</Link>
          <h1>{t('envTitle')}</h1>
          <span className="row">
            <button className="primary" onClick={() => printSides(settings.plain ? 1 : 0)}>{t('envPrint')}</button>
            <TipButton />
            <SignOutButton />
          </span>
        </div>

        <ul className="env-options" role="radiogroup" aria-label={t('envChoose')}>
          {options.map((o) => (
            <li key={o.id}>
              <button role="radio" aria-checked={o.id === env.id} className={`env-option${o.id === env.id ? ' on' : ''}`}
                onClick={() => update({ style: o.id })}>
                <EnvelopeThumb env={o.env} />
                <strong>{t(`env_${o.id}` as StringKey)}</strong>
                <span className="env-tags">
                  {o.env.scissors ? <span>✂ {t('envScissors')}</span> : <span>{t('envNoScissors')}</span>}
                  {o.env.glue.length > 0 && <span>{t('envGlue')}</span>}
                </span>
              </button>
            </li>
          ))}
        </ul>

        <div className="env-main">
          <section className="env-preview">
            <span className="seg" role="group" aria-label={t('envChoose')}>
              {(['front', 'back', 'side1', 'side2'] as const).map((k) => (
                <button key={k} aria-pressed={tab === k} onClick={() => setTab(k)}>
                  {t(({ front: 'envFront', back: 'envBack', side1: 'envSide1', side2: 'envSide2' } as const)[k])}
                </button>
              ))}
            </span>
            <div className={`env-stage${tab === 'side1' || tab === 'side2' ? ' sheet' : ''}`}>
              {tab === 'front' && <EnvelopeView env={env} view="front" decor={decor} label={t('envFront')} />}
              {tab === 'back' && <EnvelopeView env={env} view="back" decor={decor} label={t('envBack')} />}
              {tab === 'side1' && <EnvelopeInside env={env} />}
              {tab === 'side2' && <EnvelopeOutside env={env} decor={decor} />}
            </div>
            <p className="muted small">
              {t(`envAbout_${env.id}` as StringKey)}{' '}
              {t('envFits', { w: Math.round(env.inside.w), h: Math.round(env.inside.h), cw: Math.round(long), ch: Math.round(short) })}
            </p>
          </section>

          <aside className="env-side">
            <label className="stack-label">{t('envName')}
              <input value={name} maxLength={40} placeholder={t('envNamePlaceholder')} onChange={(e) => update({ name: e.target.value })} />
            </label>
            <div>
              <span className="small strong">{t('envOutside')}</span>
              <span className="seg" role="group" aria-label={t('envOutside')}>
                <button aria-pressed={!settings.plain} onClick={() => update({ plain: false })}>{t('envMatchCard')}</button>
                <button aria-pressed={!!settings.plain} onClick={() => update({ plain: true })}>{t('envPlain')}</button>
              </span>
            </div>
            <div>
              <span className="small strong">{t('envSteps')}</span>
              <ol className="env-steps">
                {env.steps.map((s) => <li key={s}>{t(s)}</li>)}
              </ol>
            </div>
            <div className="env-print">
              <button className="primary big-btn" onClick={() => printSides(settings.plain ? 1 : 0)}>{t('envPrint')}</button>
              {!settings.plain && (
                <span className="print-one small">
                  <span className="muted">{t('printNoDuplex')}</span>
                  <button className="small" onClick={() => printSides(1)}>{t('printOne1')}</button>
                  <button className="small" onClick={() => printSides(2)} title={t('printOneFlip')}>{t('printOne2')}</button>
                </span>
              )}
              <p className="muted small">{t('envPrintHint')}</p>
            </div>
            {store && <p className="muted small">{store}</p>}
            <p className="tip small">{t('envTrial')}</p>
            {saveError && <p className="error small" role="alert">{saveError}</p>}
          </aside>
        </div>
      </main>

      {/* the sheet as it goes to the printer: side 1, then side 2 on its back */}
      {(!only || only === 1) && (
        <section className="print-sheet env-print-sheet" style={{ width: `${W}mm`, height: `${H}mm` }}>
          <EnvelopeInside env={env} className="env-svg print" />
        </section>
      )}
      {!settings.plain && (!only || only === 2) && (
        <section className="print-sheet env-print-sheet" style={{ width: `${W}mm`, height: `${H}mm` }}>
          <EnvelopeOutside env={env} decor={decor} className="env-svg print" />
        </section>
      )}
    </div>
  );
}
